import { computed, ref } from 'vue'
import { useConfigName } from '@/composables/useConfigName'
import { DeviceItem, useConfigurationStore, useLocaleStore } from '@/stores'
import { useMacroRepository } from '@/views/device/keyboard/keys/components/TabMacro/composables/useMacroRepository'
import { useMyKeysConf } from '@/views/device/keyboard/keys/composables/useMyKeysConf'
import type { KeySetting as KeyboardKeySetting } from '@/views/device/keyboard/keys/types'
import {
  getLocalizedField,
  KEYCODE_KEYWORDS_MAP,
  parseLayerMapToKeySettings,
} from '@/views/device/keyboard/keys/utils/keyMapSettings'
import { SPECIAL_KEY_META_BY_KEYWORDS } from '@/views/device/keyboard/keys/utils/specialKeyMeta'
import { useKeyLabel } from '@/views/device/mouse/keys/composables/useKeyLabel'
import { useKeySDK } from '@/views/device/mouse/keys/composables/useKeySDK'
import { useMouseMacroRepository } from '@/views/device/mouse/keys/composables/useMouseMacroRepository'
import type { KeySetting as MouseKeySetting } from '@/views/device/mouse/keys/types'
import { parseMapToKeySettings } from '@/views/device/mouse/keys/utils/keyMapSetting'
import type { KeyConfig } from '@rapoo/element'

const OVERVIEW_KEYBOARD_KEY_TYPES = new Set(['normal', 'media', 'configuration', 'macro'])

type MacroNamedSetting = {
  keyType?: string
  raw?: {
    macro?: { index?: number }
    macroName?: string
  }
}

export function useKeyRemap(itemRef: { value: DeviceItem }) {
  const { t } = useLocaleStore()
  const configStore = useConfigurationStore()

  const mouseKeySettings = ref<MouseKeySetting[]>([])
  const keyboardKeySettings = ref<KeyboardKeySetting[]>([])

  const device = computed(() => itemRef.value.original)
  const layout = computed(() => device.value?.manifest.layout)
  const mouseKeys = computed(() => layout.value?.keys[0] || [])

  const { initKeys: initMouseKeys } = useKeySDK()
  const { initKeys: initKeyboardKeys } = useMyKeysConf()
  const keyboardMacroRepo = useMacroRepository()
  const mouseMacroRepo = useMouseMacroRepository()

  /**
   * 将 keySettings 中宏占位名（"宏 {index}"）替换为宏列表中的真实名称。
   * 列表未命中时再按 index 读取单条宏名称兜底。
   */
  const resolveMacroNames = async (
    settings: MacroNamedSetting[],
    repo: {
      macroList: { value: { index: number; name: string }[] }
      loadMacroList: () => Promise<void>
      readMacroByIndex: (index: number) => Promise<{ name: string }>
    },
  ) => {
    const macroSettings = settings.filter((s) => s.keyType === 'macro' && s.raw?.macro != null)
    if (!macroSettings.length) return

    await repo.loadMacroList()
    const nameMap = new Map(repo.macroList.value.map((m) => [Number(m.index), m.name]))

    await Promise.all(
      macroSettings.map(async (setting) => {
        const index = Number(setting.raw?.macro?.index)
        if (!Number.isFinite(index)) return

        let realName = nameMap.get(index)?.trim()
        if (!realName) {
          try {
            const macro = await repo.readMacroByIndex(index)
            realName = macro.name?.trim()
          } catch {
            realName = undefined
          }
        }

        if (realName && setting.raw) {
          setting.raw.macroName = realName
        }
      }),
    )
  }

  const getKeyboardOriginalLabel = (keywords: string) => {
    const config = KEYCODE_KEYWORDS_MAP.get(keywords)
    if (!config) return keywords
    return getLocalizedField(config.alias) || getLocalizedField(config.name) || keywords
  }

  const getKeyboardMediaMeta = (setting: KeyboardKeySetting) => {
    const keywords = (setting.target as KeyConfig | undefined)?.keywords || ''
    return SPECIAL_KEY_META_BY_KEYWORDS.get(keywords)
  }

  const getKeyboardTargetLabel = (setting: KeyboardKeySetting) => {
    if (setting.keyType === 'macro') {
      return setting.raw?.macroName || t('keys.targetPanel.macroFallback')
    }

    const target = setting.target as KeyConfig | undefined
    const keywords = target?.keywords || ''

    if (keywords === 'Disable key') {
      return t('keys.normal.disableKey')
    }

    if (setting.keyType === 'configuration') {
      if (keywords === 'prev config') return t('keys.config.prevConfig')
      if (keywords === 'next config') return t('keys.config.nextConfig')

      const match = /^config(\d+)$/.exec(keywords)
      if (match) {
        const idx = Number(match[1])
        const cfg = configStore.configs?.[idx]
        if (cfg) return useConfigName(cfg, device.value)
        return `${t('keys.config.profile')} ${String.fromCharCode(65 + idx)}`
      }
    }

    if (setting.keyType === 'media') {
      const labelKey = getKeyboardMediaMeta(setting)?.labelKey
      if (labelKey) return t(labelKey)
    }

    return getLocalizedField(target?.alias ?? target?.name) || keywords
  }

  const getKeyboardTargetIcon = (setting: KeyboardKeySetting) => {
    if (setting.keyType !== 'media') return undefined
    const icon = setting.raw?.icon || getKeyboardMediaMeta(setting)?.icon
    return typeof icon === 'string' && icon ? icon : undefined
  }

  const getMouseKeySettings = async () => {
    const keyMap = await initMouseKeys()
    let settings = parseMapToKeySettings({
      keyMap,
      layoutKeys: layout.value?.keys,
    })

    if (settings.some((s) => s.keyType === 'macro')) {
      await resolveMacroNames(settings, mouseMacroRepo)
      settings = [...settings]
    }

    mouseKeySettings.value = settings
  }

  const getKeyboardKeySettings = async () => {
    const keyMap = await initKeyboardKeys()
    const layoutKeys = layout.value?.keys ?? []
    // 传入 model：V700diy-87 三个灯效功能键出厂默认视为“未改键”，概览页不应默认展示。
    let settings = parseLayerMapToKeySettings({
      keyMap,
      layoutKeys,
      model: device.value?.manifest.model,
    }).filter((s) => OVERVIEW_KEYBOARD_KEY_TYPES.has(s.keyType || '') && s.keywords !== 'Fn')

    if (settings.some((s) => s.keyType === 'macro')) {
      await resolveMacroNames(settings, keyboardMacroRepo)
      settings = [...settings]
    }

    keyboardKeySettings.value = settings
  }

  const initKeyRemapData = async () => {
    const type = device.value.manifest.type
    if (type === 'mouse') {
      keyboardKeySettings.value = []
      await getMouseKeySettings()
      return
    }
    if (type === 'keyboard') {
      mouseKeySettings.value = []
      await getKeyboardKeySettings()
      return
    }
    mouseKeySettings.value = []
    keyboardKeySettings.value = []
  }

  const { getKeyLabel } = useKeyLabel(mouseKeySettings)

  const keyRemapData = computed(() => {
    const type = device.value.manifest.type

    if (type === 'mouse') {
      const data = mouseKeys.value.reduce(
        (list, mouseKey) => {
          const { originalLabel, targetLabel } = getKeyLabel(mouseKey, false)
          if (targetLabel) {
            list.push({
              name: originalLabel,
              value: targetLabel,
            })
          } else {
            list.push({
              name: originalLabel,
              value: originalLabel,
            })
          }
          return list
        },
        [] as { name: string; value: string; valueIcon?: string }[],
      )

      if (!data.length) return null

      return {
        title: t('home.overview.keyRemapData.title'),
        data,
        scrollable: true,
      }
    }

    if (type === 'keyboard') {
      const data = keyboardKeySettings.value.map((setting) => ({
        name: getKeyboardOriginalLabel(setting.keywords),
        value: getKeyboardTargetLabel(setting),
        valueIcon: getKeyboardTargetIcon(setting),
      }))

      if (!data.length) return null

      return {
        title: t('home.overview.keyRemapData.title'),
        data,
        scrollable: true,
      }
    }

    return null
  })

  return {
    keyRemapData,
    initKeyRemapData,
  }
}
